import type { ChatElementInspectionPresentationPayload, FileDiff } from '@/jsb';

export type ConversationMessageFileRef = {
  refId?: string;
  kind?: string;
  source?: string;
  environmentId?: string;
  path?: string;
  name?: string;
  mimeType?: string;
  sizeBytes?: number;
  contentHash?: string;
  displayURL?: string;
  downloadURL?: string;
  thumbnailURL?: string;
  publicShareURL?: string;
  authRequired?: boolean;
  authScope?: string;
  sourceSessionId?: string;
  agentId?: string;
  signature?: string;
  expiresAt?: number;
};

export type ConversationMessageQuoteReference = {
  sourceMessageId: string;
  startOffset: number;
  endOffset: number;
};

export type ConversationMessageAppRequestHostAccess = {
  environmentId?: string;
  environmentLabel?: string;
  description?: string;
  status?: string;
  approved?: boolean;
};

export type ConversationMessageAppRequestComputerUseStart = {
  environmentId?: string;
  environmentLabel?: string;
  availableModes?: string[];
  apps?: string[];
  status?: string;
  approved?: boolean;
  mode?: string;
};

export type ConversationMessageContent = {
  type: string;
  text?: string;
  title?: string;
  url?: string;
  displayURL?: string;
  displayUrl?: string;
  display_url?: string;
  downloadURL?: string;
  downloadUrl?: string;
  download_url?: string;
  thumbnailURL?: string;
  thumbnailUrl?: string;
  thumbnail_url?: string;
  publicShareURL?: string;
  publicShareUrl?: string;
  public_share_url?: string;
  authRequired?: boolean;
  auth_required?: boolean;
  authScope?: string;
  auth_scope?: string;
  siteName?: string;
  entrypointPath?: string;
  verifiedStatus?: string;
  fileRef?: ConversationMessageFileRef;
  fileRefs?: ConversationMessageFileRef[];
  fileName?: string;
  mimeType?: string;
  sizeBytes?: number;
  entryKind?: string;
  generationPrompt?: string;
  quoteRef?: ConversationMessageQuoteReference;
  requestId?: string;
  requestType?: string;
  status?: string;
  triggerClientInstanceId?: string;
  authorizationURL?: string;
  loginURL?: string;
  provider?: string;
  alias?: string;
  reason?: string;
  startURL?: string;
  description?: string;
  environmentId?: string;
  environmentLabel?: string;
  hostAccess?: ConversationMessageAppRequestHostAccess;
  computerUseStart?: ConversationMessageAppRequestComputerUseStart;
  /** `type: "memory"` only — the cloud/local memory id, for the card's "Open
   * Memory" affordance. Display-only; never part of the model-facing payload. */
  memoryId?: string;
};

export type ConversationMessageAttachmentImageSize = {
  width: number;
  height: number;
};

export type ConversationMessageAttachmentRenderCache = {
  imageSize?: ConversationMessageAttachmentImageSize;
};

export type ConversationMessageRenderCache = {
  attachments?: Record<string, ConversationMessageAttachmentRenderCache>;
};

export type ConversationAttachmentImageLayoutHandler = (
  messageId: string,
  attachmentKey: string,
  imageSize: ConversationMessageAttachmentImageSize
) => void;

export type ConversationMessageQuestionInfo = {
  question: string;
  header?: string;
  options: ConversationMessageQuestionOption[];
  multiSelect?: boolean;
};

export type ConversationMessageQuestionOption = {
  label: string;
  description?: string;
};

export type ConversationMessageQuestionReplyInfo = {
  reply?: unknown;
  cancelled?: boolean;
};

export type ConversationMessageSaveFileRequestInfo = {
  environmentId: string;
  path: string;
  fileName?: string;
  mimeType?: string;
  title?: string;
  message?: string;
  size?: number;
};

export type ConversationMessageSaveFileReplyInfo = {
  approved?: boolean;
  cancelled?: boolean;
  fileName?: string;
  mimeType?: string;
  bytesWritten?: number;
};

export type ConversationMessageSecretInputInfo = {
  prompt: string;
  label?: string;
  slot?: string;
};

export type ConversationMessageSecretInputReplyInfo = {
  provided: boolean;
  cancelled?: boolean;
};

export type ConversationMessageScheduleReference = {
  scheduleId: string;
  title: string;
  subtitle?: string;
  isPaused?: boolean;
  hasError?: boolean;
};

export type ConversationMessage = {
  id: string;
  type: string;
  role?: string;
  timestamp: number;
  content?: ConversationMessageContent[];
  messageId?: string;
  sandboxId?: string;
  acceptedSummary?: string;
  reviewDiff?: FileDiff[];
  reviewDiffTotal?: number;
  sourceSessionId?: string;
  sourceConversationId?: string;
  sourceTitle?: string;
  sourceAgentName?: string;
  senderName?: string;
  senderAvatarURL?: string;
  confirmationId?: string;
  traceparent?: string;
  tracestate?: string;
  question?: ConversationMessageQuestionInfo;
  questionReply?: ConversationMessageQuestionReplyInfo;
  saveFileRequest?: ConversationMessageSaveFileRequestInfo;
  saveFileReply?: ConversationMessageSaveFileReplyInfo;
  secretInput?: ConversationMessageSecretInputInfo;
  secretInputReply?: ConversationMessageSecretInputReplyInfo;
  schedule?: ConversationMessageScheduleReference;
  toolUseId?: string;
  errorType?: string;
  error?: string;
  renderCache?: ConversationMessageRenderCache;
  metadata?: unknown;
};

export type BrowserElementInspectionPresentation =
  ChatElementInspectionPresentationPayload & {
    attributes?: string;
    boundingRect?: string;
  };

function normalizedNonEmpty(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

function isUsableImageSize(
  size: ConversationMessageAttachmentImageSize | null | undefined
): size is ConversationMessageAttachmentImageSize {
  return (
    typeof size?.width === 'number' &&
    typeof size.height === 'number' &&
    Number.isFinite(size.width) &&
    Number.isFinite(size.height) &&
    size.width > 0 &&
    size.height > 0
  );
}

export function conversationMessageAttachmentKey(contentIndex: number): string {
  return `content:${contentIndex}`;
}

export function conversationMessageRenderIdentity(
  message: Pick<ConversationMessage, 'id' | 'messageId'>
): string {
  return normalizedNonEmpty(message.messageId) ?? message.id;
}

export function conversationMessageCachedAttachmentImageSize(
  message: Pick<ConversationMessage, 'renderCache'>,
  attachmentKey: string
): ConversationMessageAttachmentImageSize | undefined {
  const size = message.renderCache?.attachments?.[attachmentKey]?.imageSize;
  return isUsableImageSize(size) ? size : undefined;
}

export function withConversationMessageAttachmentImageSize<
  T extends ConversationMessage,
>(
  message: T,
  attachmentKey: string,
  imageSize: ConversationMessageAttachmentImageSize
): T {
  const normalizedKey = normalizedNonEmpty(attachmentKey);
  if (!normalizedKey || !isUsableImageSize(imageSize)) {
    return message;
  }

  const current = message.renderCache?.attachments?.[normalizedKey]?.imageSize;
  if (
    current?.width === imageSize.width &&
    current.height === imageSize.height
  ) {
    return message;
  }

  return {
    ...message,
    renderCache: {
      ...message.renderCache,
      attachments: {
        ...message.renderCache?.attachments,
        [normalizedKey]: {
          ...message.renderCache?.attachments?.[normalizedKey],
          imageSize,
        },
      },
    },
  };
}

export function withMergedConversationMessageRenderCache<
  T extends ConversationMessage,
>(message: T, previous: ConversationMessage | undefined): T {
  const previousAttachments = previous?.renderCache?.attachments;
  if (!previousAttachments || Object.keys(previousAttachments).length === 0) {
    return message;
  }

  return {
    ...message,
    renderCache: {
      ...message.renderCache,
      attachments: {
        ...previousAttachments,
        ...message.renderCache?.attachments,
      },
    },
  };
}

export function mergeConversationMessageRenderCaches<
  T extends ConversationMessage,
>(messages: T[], previousMessages: ConversationMessage[]): T[] {
  return messages.map(message => {
    const previous = previousMessages.find(
      item =>
        item.id === message.id ||
        (normalizedNonEmpty(item.messageId) !== null &&
          normalizedNonEmpty(item.messageId) ===
            normalizedNonEmpty(message.messageId))
    );
    return withMergedConversationMessageRenderCache(message, previous);
  });
}

export function updateConversationMessagesAttachmentImageSize<
  T extends ConversationMessage,
>(
  messages: T[],
  messageId: string,
  attachmentKey: string,
  imageSize: ConversationMessageAttachmentImageSize
): T[] {
  const normalizedMessageId = normalizedNonEmpty(messageId);
  if (!normalizedMessageId) {
    return messages;
  }

  const index = messages.findIndex(
    message =>
      message.id === normalizedMessageId ||
      normalizedNonEmpty(message.messageId) === normalizedMessageId
  );
  if (index === -1) {
    return messages;
  }

  const updatedMessage = withConversationMessageAttachmentImageSize(
    messages[index],
    attachmentKey,
    imageSize
  );
  if (updatedMessage === messages[index]) {
    return messages;
  }

  const next = [...messages];
  next[index] = updatedMessage;
  return next;
}

export function conversationMessageFromBridgeMessage<
  T extends ConversationMessage,
>(message: T): ConversationMessage {
  return message;
}

export function conversationMessagesFromBridgeMessages<
  T extends ConversationMessage,
>(messages: T[]): ConversationMessage[] {
  return messages.map(conversationMessageFromBridgeMessage);
}

export function isUserMessage(msg: ConversationMessage): boolean {
  return msg.type === 'message' && msg.role === 'user';
}

export function isAssistantMessage(msg: ConversationMessage): boolean {
  return msg.type === 'message' && msg.role === 'assistant';
}

export function isMessageStartMessage(msg: ConversationMessage): boolean {
  return msg.type === 'message_start';
}

export function isSandboxReviewMessage(msg: ConversationMessage): boolean {
  return msg.type === 'sandbox_review';
}

export function isQuestionMessage(msg: ConversationMessage): boolean {
  return msg.type === 'question';
}

export function isQuestionReplyMessage(msg: ConversationMessage): boolean {
  return msg.type === 'question_reply';
}

export function isSaveFileRequestMessage(msg: ConversationMessage): boolean {
  return msg.type === 'save_file_request';
}

export function isSaveFileReplyMessage(msg: ConversationMessage): boolean {
  return msg.type === 'save_file_reply';
}

export function isSecretInputMessage(msg: ConversationMessage): boolean {
  return msg.type === 'secret_input';
}

export function isSecretInputReplyMessage(msg: ConversationMessage): boolean {
  return msg.type === 'secret_input_reply';
}

export function isScheduleMessage(msg: ConversationMessage): boolean {
  return msg.type === 'schedule';
}

export function isToolMessage(msg: ConversationMessage): boolean {
  return msg.type === 'message' && msg.role === 'tool';
}

export function isAppRequestMessage(msg: ConversationMessage): boolean {
  return (
    msg.type === 'message' &&
    msg.role === 'system' &&
    (msg.content ?? []).some(content => content.type === 'app_request')
  );
}

export function isErrorMessage(msg: ConversationMessage): boolean {
  return !!msg.error;
}

export function getTextContent(msg: ConversationMessage): string[] {
  if (!msg.content) return [];
  return msg.content.filter(c => c.type === 'text' && c.text).map(c => c.text!);
}

const leadingSystemReminderBlockRegex =
  /^\s*<system-reminder>[\s\S]*?<\/system-reminder>\s*/;
const leadingCompactedContextBlockRegex =
  /^\s*<compacted-context>[\s\S]*?<\/compacted-context>\s*/;
const leadingAppRequestTagRegex = /^\s*(?:<app-request\b[^>]*\/>\s*)+/;
const browserElementInspectionTagRegex =
  /\s*<browser-element-inspection\s+id="([^"]+)"\s*\/>\s*/g;
const browserElementContextBlockRegex =
  /\s*<browser_element_context>[\s\S]*?<\/browser_element_context>\s*/g;
const userReminderBlockRegex = /<user-reminder>[\s\S]*?<\/user-reminder>\s*/g;
const trailingSuggestionBlockRegex =
  /\s*(?:<suggestion>[\s\S]*?<\/suggestion>\s*)+$/;

export function stripLeadingSystemReminderBlock(text: string): string {
  return text.replace(leadingSystemReminderBlockRegex, '');
}

export function stripLeadingCompactedContextBlock(text: string): string {
  return text.replace(leadingCompactedContextBlockRegex, '');
}

export function stripUserReminderBlocks(text: string): string {
  return text
    .replace(userReminderBlockRegex, '')
    .replace(/^\s+/, '')
    .replace(/\s+$/, '')
    .replace(/\n{3,}/g, '\n\n');
}

export function stripLeadingAppRequestTags(text: string): string {
  return text.replace(leadingAppRequestTagRegex, '');
}

export function stripBrowserElementInspectionTags(text: string): string {
  return text
    .replace(browserElementInspectionTagRegex, '')
    .replace(browserElementContextBlockRegex, '')
    .replace(/\s+$/, '');
}

export function stripTrailingSuggestionBlock(text: string): string {
  return text.replace(trailingSuggestionBlockRegex, '');
}

function browserElementInspectionContext(text: string): string | undefined {
  const match =
    /<browser_element_context>\s*([\s\S]*?)\s*<\/browser_element_context>/.exec(
      text
    );
  return match?.[1]?.trim();
}

function browserElementInspectionMarkerId(text: string): string | undefined {
  const regex = new RegExp(browserElementInspectionTagRegex.source, 'g');
  return regex.exec(text)?.[1];
}

export function getBrowserElementInspectionId(
  msg: ConversationMessage
): string | undefined {
  for (const text of getTextContent(msg)) {
    const markerId = browserElementInspectionMarkerId(text);
    if (markerId) {
      return markerId;
    }
  }
  if (getTextContent(msg).some(text => browserElementInspectionContext(text))) {
    return `browser-element-${msg.messageId ?? msg.id}`;
  }
  return undefined;
}

function contextField(context: string, label: string): string | undefined {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = new RegExp(`^${escaped}:\\s*(.+)$`, 'm').exec(context);
  return match?.[1]?.trim();
}

function contextSection(
  context: string,
  label: string,
  nextLabel: string
): string | undefined {
  const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const escapedNextLabel = nextLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = new RegExp(
    `${escapedLabel}:\\s*\\n([\\s\\S]*?)(?:\\n\\s*${escapedNextLabel}:|$)`
  ).exec(context);
  return match?.[1]?.trim();
}

function contextHtmlExcerpt(context: string): string | undefined {
  const fenced = /Element HTML excerpt:\s*\n```html\s*\n?([\s\S]*?)\n?```/.exec(
    context
  );
  if (fenced?.[1]) {
    return fenced[1].trim();
  }

  return contextSection(context, 'Element HTML excerpt', 'END');
}

export function getBrowserElementInspectionPresentation(
  msg: ConversationMessage
): BrowserElementInspectionPresentation | undefined {
  const texts = getTextContent(msg);
  const contextText = texts
    .map(text => browserElementInspectionContext(text))
    .find((context): context is string => !!context);
  if (!contextText) {
    return undefined;
  }

  const inspectionId =
    texts.map(browserElementInspectionMarkerId).find(Boolean) ??
    `browser-element-${msg.messageId ?? msg.id}`;
  const elementText = contextSection(
    contextText,
    'Element text',
    'Element HTML excerpt'
  );

  return {
    inspectionId,
    userMessage: getUserDisplayTextContent(msg).join('\n').trim(),
    pageTitle: contextField(contextText, 'Page title'),
    pageURL: contextField(contextText, 'Page URL'),
    selector: contextField(contextText, 'Selector') ?? 'Selected element',
    tagName: contextField(contextText, 'Tag') ?? 'element',
    attributes: contextField(contextText, 'Attributes'),
    boundingRect: contextField(contextText, 'Bounding rect'),
    elementText,
    elementHtml: contextHtmlExcerpt(contextText),
    screenshotDataURL: undefined,
    screenshotStatus: 'unavailable',
    updatedAt: msg.timestamp,
  };
}

export function isCompactedContextMessage(msg: ConversationMessage): boolean {
  if (msg.role !== 'user') return false;
  const texts = getTextContent(msg);
  if (texts.length === 0) return false;
  const stripped = stripUserReminderBlocks(
    stripLeadingSystemReminderBlock(texts[0])
  );
  return stripped.trimStart().startsWith('<compacted-context>');
}

export function getUserDisplayTextContent(msg: ConversationMessage): string[] {
  const texts = getTextContent(msg);
  return texts
    .map((text, index) => {
      if (msg.role !== 'user') {
        return index === 0 ? stripLeadingAppRequestTags(text) : text;
      }

      const withoutReminder =
        index === 0 ? stripLeadingSystemReminderBlock(text) : text;
      const withoutUserReminders = stripUserReminderBlocks(withoutReminder);
      const withoutCompactedContext =
        index === 0
          ? stripLeadingCompactedContextBlock(withoutUserReminders)
          : withoutUserReminders;
      const withoutBrowserElementInspection = stripBrowserElementInspectionTags(
        withoutCompactedContext
      );
      const withoutTrailingSuggestion =
        index === texts.length - 1
          ? stripTrailingSuggestionBlock(withoutBrowserElementInspection)
          : withoutBrowserElementInspection;
      return withoutTrailingSuggestion;
    })
    .filter(text => text.length > 0);
}
